import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getRequests, rejectRequest } from "@/api/requestService";
import type { RequestResponse, GetRequestsParams } from "@/types/request";
import Pagination from "@/components/Pagination";
import SearchBar from "@/components/SearchBar";
import Loading from "@/components/Loading";
import { toast } from "react-toastify";
import useAuthStore from "@/store/useAuthStore";
import { Dialog, DialogTitle, DialogContent, Typography, Box, Button } from "@mui/material";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

const AdminRequests: React.FC = () => {
    const { isDark } = useDashboardTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const role = useAuthStore((state) => state.role);
    const canManageRequests = role === "SUPER_ADMIN" || role === "ADMIN";

    // URL state
    const page = parseInt(searchParams.get("page") || "1", 10);
    const pageSize = parseInt(searchParams.get("pageSize") || "10", 10);
    const requestContext = searchParams.get("requestContext") || "REGISTRATION";
    const status = searchParams.get("status") || "PENDING";
    const searchStr = searchParams.get("search") || "";
    const sortBy = searchParams.get("sortBy") || "createdAt";
    const sortOrder = (searchParams.get("sortOrder") as "asc" | "desc") || "desc";

    const [data, setData] = useState<RequestResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal state for rejection
    const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
    const [selectedRejectId, setSelectedRejectId] = useState<string | null>(null);
    const [isRejecting, setIsRejecting] = useState(false);

    const [localSearch, setLocalSearch] = useState(searchStr);

    const fetchRequests = async () => {
        setLoading(true);
        setError(null);
        try {
            const params: GetRequestsParams = {
                page,
                pageSize,
                requestContext,
                status,
                search: searchStr,
                sortBy,
                sortOrder,
            };
            const res = await getRequests(params);

            // Handle edge case where if we delete the last item on a page, fetch previous
            if (res.data?.items?.length === 0 && page > 1) {
                handleParamChange("page", (page - 1).toString());
                return;
            }

            setData(res);
        } catch (err: any) {
            console.error("Failed to fetch requests", err);
            // Access denied handled here typically or in interceptor
            if (err.response?.status === 401 || err.response?.status === 403) {
                setError("Access Denied");
            } else {
                setError(err.message || "An error occurred while fetching requests.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, requestContext, status, searchStr, sortBy, sortOrder]);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (localSearch !== searchStr) {
                handleParamChange("search", localSearch);
            }
        }, 500);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localSearch]);

    const handleParamChange = (key: string, value: string) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            if (value) {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
            // Reset page to 1 on filter change
            if (key !== "page") {
                newParams.set("page", "1");
            }
            return newParams;
        });
    };

    const toggleSort = (column: string) => {
        if (sortBy === column) {
            handleParamChange("sortOrder", sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set("sortBy", column);
                newParams.set("sortOrder", "desc");
                newParams.set("page", "1");
                return newParams;
            });
        }
    };

    const handleRejectClick = (id: string) => {
        setSelectedRejectId(id);
        setRejectConfirmOpen(true);
    };

    const handleConfirmReject = async () => {
        if (!selectedRejectId) return;
        setIsRejecting(true);
        try {
            await rejectRequest(selectedRejectId);
            toast.success("Request rejected and email purged.");
            setRejectConfirmOpen(false);
            setSelectedRejectId(null);
            fetchRequests();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to reject request");
        } finally {
            setIsRejecting(false);
        }
    };

    const handleCancelReject = () => {
        setRejectConfirmOpen(false);
        setSelectedRejectId(null);
    };

    if (error === "Access Denied") {
        return (
            <div className="flex justify-center items-center h-full pt-10 text-white">
                <h2 className="text-2xl font-bold">Access Denied</h2>
            </div>
        );
    }

    return (
        <div className={"p-6 min-h-screen " + (isDark ? "bg-[#040404] text-white" : "light-theme-dashboard")} style={{ background: isDark ? "" : "#F4F5F7" }}>
            <div className="mb-6 px-4 py-3 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-200 text-sm">
                <strong>Warning:</strong> Reject will permanently purge related account data for registration requests.
            </div>

            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Account Requests</h1>
                <div className="w-1/3">
                    <SearchBar
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* Error Output */}
            {error && error !== "Access Denied" && (
                <div className="mb-4 text-red-500">{error}</div>
            )}

            {/* Table */}
            <div className="overflow-x-auto bg-[#1A1A1A] rounded-lg border border-gray-800">
                <table className="w-full text-left">
                    <thead className="bg-[#2A2A2A] text-gray-400 text-sm">
                        <tr>
                            <th className="px-6 py-4 font-medium">Company Name</th>
                            <th
                                className="px-6 py-4 font-medium cursor-pointer flex items-center gap-1 hover:text-white"
                                onClick={() => toggleSort("companyEmail")}
                            >
                                Company Email
                                {sortBy === "companyEmail" && (sortOrder === "asc" ? "↑" : "↓")}
                            </th>
                            <th className="px-6 py-4 font-medium">Request Type</th>
                            <th className="px-6 py-4 font-medium">Description</th>
                            <th
                                className="px-6 py-4 font-medium cursor-pointer space-x-1 hover:text-white"
                                onClick={() => toggleSort("createdAt")}
                            >
                                <span>Created At</span>
                                {sortBy === "createdAt" && (
                                    <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                                )}
                            </th>
                            <th className="px-6 py-4 font-medium">Status</th>
                            {canManageRequests && (
                                <th className="px-6 py-4 font-medium">Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={canManageRequests ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                                    <Loading />
                                </td>
                            </tr>
                        ) : !data || !data.data || data.data.items.length === 0 ? (
                            <tr>
                                <td colSpan={canManageRequests ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                                    {status === "PENDING" && requestContext === "REGISTRATION"
                                        ? "No pending registration requests"
                                        : "No requests found"}
                                </td>
                            </tr>
                        ) : (
                            data.data.items.map((req) => (
                                <tr
                                    key={req.id}
                                    className="border-b border-gray-800 hover:bg-[#2A2A2A] transition-colors"
                                >
                                    <td className="px-6 py-4 text-sm">{req.companyName}</td>
                                    <td className="px-6 py-4 text-sm">{req.companyEmail}</td>
                                    <td className="px-6 py-4 text-sm">{req.requestType}</td>
                                    <td className="px-6 py-4 text-sm max-w-[200px] truncate" title={req.description}>
                                        {req.description || "-"}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        {new Date(req.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm">
                                        <span
                                            className={`px-2 py-1 rounded text-xs ${req.status === "PENDING"
                                                ? "bg-yellow-500/20 text-yellow-500"
                                                : req.status === "APPROVED"
                                                    ? "bg-green-500/20 text-green-500"
                                                    : "bg-red-500/20 text-red-500"
                                                }`}
                                        >
                                            {req.status}
                                        </span>
                                    </td>
                                    {canManageRequests && (
                                        <td className="px-6 py-4 text-sm flex gap-2">
                                            {req.status === "PENDING" && (
                                                <button
                                                    onClick={() => handleRejectClick(req.id)}
                                                    className="px-3 py-1 bg-[#2B1E22] hover:bg-[#352227] border border-[#3A2B31] text-[#FCA5A5] rounded text-sm transition"
                                                >
                                                    Reject & Purge
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!loading && data && data.data && data.data.totalPages > 1 && (
                <Pagination
                    totalPages={data.data.totalPages}
                    currentPage={page}
                    setCurrentPage={(p) => handleParamChange("page", p.toString())}
                />
            )}

            {/* Confirm Rejection Modal */}
            <Dialog
                open={rejectConfirmOpen}
                onClose={handleCancelReject}
                PaperProps={{ sx: { minWidth: "400px", backgroundColor: "#1E1D2A", color: "white" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", color: "#FCA5A5" }}>
                    Confirm Reject & Purge
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 3, fontSize: "0.95rem", color: "#D1D5DB", lineHeight: 1.5 }}>
                        This will delete request records for this email and remove any existing user data for this email. User can re-request with the same email.
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        {isRejecting ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                <span>Purging...</span>
                            </div>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelReject}
                                    sx={{
                                        color: "#D1D5DB",
                                        borderColor: "#4B5563",
                                        "&:hover": { backgroundColor: "#374151" },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleConfirmReject}
                                    sx={{
                                        backgroundColor: "#EF4444",
                                        color: "white",
                                        fontWeight: "bold",
                                        "&:hover": { backgroundColor: "#DC2626" },
                                    }}
                                >
                                    Reject & Purge
                                </Button>
                            </>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminRequests;
